import { FALLBACK_IMAGE_URL } from 'components/UI/FallbackImage/FallbackImage';
import type { AllowedCropTypes, ApiImageProps } from 'components/UI/Image';
import type { AreaItem, BaseNestedObject } from 'types/cms';
import { getFilename, normalizeBaseUrl } from 'utils/image';
import { getImageLoader } from 'utils/imageLoader';
import { slugify } from 'utils/string';
import type { ImageObject, PublisherObject } from '../SchemaObjects.interface';

export const getSeoTitle = (primary?: string, secondary?: string): string => {
  if (!primary && !secondary) {
    return '';
  }

  if (!secondary && primary) {
    return primary;
  }

  return secondary + (primary ? ': ' + primary : '');
};

export const rtlPublisherObject: PublisherObject = {
  '@type': 'Organization',
  name: 'RTL.de',
  url: 'https://www.rtl.de',
  logo: {
    '@type': 'ImageObject',
    url: 'https://www.rtl.de/rtl_logo.png',
    width: 1200,
    height: 630,
  },
};

export const rtlFallbackImageObject: ImageObject = {
  '@type': 'ImageObject',
  url: FALLBACK_IMAGE_URL,
  width: 1200,
  height: 675,
};

export const getImageObject = (image: ApiImageProps, overwritecrop?: AllowedCropTypes): ImageObject => {
  if (!image) {
    return rtlFallbackImageObject;
  }

  return {
    '@type': 'ImageObject',
    url: getImageLoader(
      image.baseUrl,
      getFilename(image.filename, image.fields?.alt?.value),
      overwritecrop ?? image.crop
    ).loader({
      src: image.baseUrl,
      width: image.originalWidth,
    }),
    width: image.originalWidth,
    height: image.originalHeight,
  };
};

export const getImageArrayObject = (image?: ApiImageProps): Array<ImageObject> => {
  if (image) {
    const width = image.originalWidth < 1200 ? image.originalWidth : 1200;
    const baseURL = `${normalizeBaseUrl(image.baseUrl)}FORMAT/${width}/${slugify(getFilename(image.filename, image.fields?.alt?.value))}.webp`;

    return [
      {
        '@type': 'ImageObject',
        url: baseURL.replace('FORMAT', 'c16_9'),
        width: width,
        height: Math.round(width * 0.5625),
      },
      {
        '@type': 'ImageObject',
        url: baseURL.replace('FORMAT', 'c1_1'),
        width: width,
        height: width,
      },
      {
        '@type': 'ImageObject',
        url: baseURL.replace('FORMAT', 'c4_3'),
        width: width,
        height: Math.round(width * 0.75),
      },
    ];
  }

  return [rtlFallbackImageObject];
};

export const createImageUrlArray = (image?: ApiImageProps, title?: string) => {
  if (image?.baseUrl) {
    const width = image.originalWidth < 1200 ? image.originalWidth : 1200;
    const filename = slugify(getFilename(title ?? image.filename, image.fields?.alt?.value));
    const baseURL = normalizeBaseUrl(image.baseUrl);

    return [
      `${baseURL}c16_9/${width}/${filename}.webp`,
      `${baseURL}c1_1/${width}/${filename}.webp`,
      `${baseURL}c4_3/${width}/${filename}.webp`,
    ];
  }

  return [FALLBACK_IMAGE_URL];
};

//eslint-disable-next-line @typescript-eslint/no-explicit-any
export const findArticleGroup: (group: BaseNestedObject) => any = (group: BaseNestedObject) => {
  let articleGroup = group.children?.find((child) => child.view === 'article');
  if (articleGroup) {
    return articleGroup;
  }
  const nestedChildren = group.children?.filter((child) => child.children);
  if (nestedChildren) {
    for (const child of nestedChildren) {
      articleGroup = findArticleGroup(child);
      if (articleGroup) {
        break;
      }
    }
  }
  return articleGroup;
};

export const getArticle = (context: ReadonlyArray<AreaItem>) => {
  let articleGroup;
  for (const item of context) {
    articleGroup = findArticleGroup(item);
    if (articleGroup) {
      break;
    }
  }
  return articleGroup;
};
